/**
 * DeviationInboxPanel.jsx
 *
 * Sidebar panel that aggregates all open Craft Standards deviations for the
 * current project from every existing analysis (Continuity Walker, Plot
 * Readiness, Completeness Report, Edit Walker) into a single actionable inbox.
 *
 * Non-redundant: existing analysis results are surfaced here with craft-
 * standard labels. Nothing is duplicated — each finding maps to exactly one
 * checklist item.
 *
 * Lives in the right-column workspace panel alongside Analysis, Arc, etc.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronUp,
  BookOpen, RefreshCw, Check, RotateCcw, ArrowRight,
  FileText, BarChart2, Layers, Edit3, X,
} from 'lucide-react';
import { craftDeviationsApi } from '@/lib/api';
import { toast } from 'sonner';

const SOURCE_META = {
  continuity:          { label: 'Continuity',         Icon: BookOpen,   color: 'text-blue-600  bg-blue-50  border-blue-200'  },
  plot_readiness:      { label: 'Plot Readiness',      Icon: BarChart2,  color: 'text-violet-600 bg-violet-50 border-violet-200' },
  completeness:        { label: 'Completeness',        Icon: Layers,     color: 'text-amber-600  bg-amber-50  border-amber-200'  },
  edit_walk:           { label: 'Edit Walker',         Icon: Edit3,      color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
};

const SEV_META = {
  high:   'border-rose-300   bg-rose-50   text-rose-700',
  medium: 'border-amber-300  bg-amber-50  text-amber-700',
  low:    'border-neutral-300 bg-neutral-50 text-neutral-600',
};

const CHECKLIST_LABELS = {
  1: 'Main plot arc', 2: 'Protagonist arc', 3: 'Antagonist arc',
  4: 'Sub-arcs', 5: "Chekhov's Guns", 6: 'Continuity',
  7: 'Pacing', 8: 'Theme proven', 9: 'Emotional throughline', 10: 'Internal consistency',
};

export default function DeviationInboxPanel({ projectId, onGoToChapter }) {
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [showResolved,  setShowResolved]  = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [resolving,     setResolving]     = useState({});

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const d = await craftDeviationsApi.list(projectId);
      setData(d);
    } catch {
      toast.error('Could not load craft deviations.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleResolve = async (devId) => {
    setResolving(p => ({ ...p, [devId]: true }));
    try {
      await craftDeviationsApi.resolve(projectId, devId);
      setData(prev => {
        if (!prev) return prev;
        const dev = prev.deviations.find(d => d.id === devId);
        return {
          ...prev,
          deviations:     prev.deviations.filter(d => d.id !== devId),
          resolved:       dev ? [...(prev.resolved || []), dev] : prev.resolved,
          total:          prev.total - 1,
          resolved_count: (prev.resolved_count || 0) + 1,
        };
      });
    } catch {
      toast.error('Could not mark deviation resolved.');
    } finally {
      setResolving(p => ({ ...p, [devId]: false }));
    }
  };

  const handleReopen = async (devId) => {
    setResolving(p => ({ ...p, [devId]: true }));
    try {
      await craftDeviationsApi.reopen(projectId, devId);
      setData(prev => {
        if (!prev) return prev;
        const dev = (prev.resolved || []).find(d => d.id === devId);
        return {
          ...prev,
          deviations:     dev ? [...prev.deviations, dev] : prev.deviations,
          resolved:       (prev.resolved || []).filter(d => d.id !== devId),
          total:          prev.total + 1,
          resolved_count: Math.max(0, (prev.resolved_count || 0) - 1),
        };
      });
    } catch {
      toast.error('Could not reopen deviation.');
    } finally {
      setResolving(p => ({ ...p, [devId]: false }));
    }
  };

  const toggleExpand = (id) =>
    setExpandedItems(p => ({ ...p, [id]: !p[id] }));

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading && !data) {
    return (
      <div className="flex h-40 items-center justify-center" data-testid="deviation-inbox-loading">
        <RefreshCw className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    );
  }

  const deviations = data?.deviations || [];
  const resolved   = data?.resolved   || [];
  const coverage   = data?.coverage   || {};

  // Group open deviations by checklist item
  const grouped = {};
  for (const dev of deviations) {
    const key = dev.checklist_item;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(dev);
  }
  const sortedItemKeys = Object.keys(grouped)
    .map(Number)
    .sort((a, b) => {
      // High severity first, then by item number
      const maxSev = (items) =>
        items.some(d => d.severity === 'high') ? 0 :
        items.some(d => d.severity === 'medium') ? 1 : 2;
      return maxSev(grouped[a]) - maxSev(grouped[b]) || a - b;
    });

  const allPassing = deviations.length === 0;

  return (
    <div className="flex h-full flex-col" data-testid="deviation-inbox-panel">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <span className="text-[13px] font-semibold text-neutral-800">Craft Deviations</span>
          {deviations.length > 0 && (
            <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white leading-none"
                  data-testid="deviation-count-badge">
              {deviations.length}
            </span>
          )}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
          title="Refresh"
          data-testid="deviation-refresh-btn"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Coverage note */}
      {!allPassing && (
        <div className="border-b border-neutral-100 bg-neutral-50 px-4 py-2">
          <p className="text-[10.5px] text-neutral-500">
            Sources active: {
              Object.entries(coverage)
                .filter(([, v]) => v)
                .map(([k]) => SOURCE_META[k]?.label || k)
                .join(' · ') || 'None yet — run an analysis first'
            }
          </p>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
           data-testid="deviation-inbox-body">
        {allPassing ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center"
               data-testid="deviation-inbox-empty">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            <p className="text-[13px] font-semibold text-emerald-700">All craft standards met</p>
            <p className="text-[11px] text-neutral-500 max-w-[200px]">
              {Object.values(coverage).some(Boolean)
                ? 'No open deviations across all active analyses.'
                : 'Run Continuity Walker or Plot Readiness to populate this inbox.'}
            </p>
          </div>
        ) : (
          sortedItemKeys.map(itemKey => (
            <ChecklistGroup
              key={itemKey}
              itemKey={itemKey}
              label={CHECKLIST_LABELS[itemKey]}
              deviations={grouped[itemKey]}
              expanded={!!expandedItems[itemKey]}
              onToggle={() => toggleExpand(itemKey)}
              onResolve={handleResolve}
              onGoToChapter={onGoToChapter}
              resolving={resolving}
            />
          ))
        )}

        {/* Resolved section */}
        {resolved.length > 0 && (
          <div className="mt-2">
            <button
              onClick={() => setShowResolved(p => !p)}
              className="flex w-full items-center justify-between py-1 text-[11px] text-neutral-500 hover:text-neutral-700"
              data-testid="show-resolved-btn"
            >
              <span>{resolved.length} resolved</span>
              {showResolved ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            {showResolved && (
              <div className="mt-1 space-y-1.5">
                {resolved.map(dev => (
                  <ResolvedRow
                    key={dev.id}
                    dev={dev}
                    onReopen={handleReopen}
                    resolving={resolving}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Craft standard reference note */}
      <div className="border-t border-neutral-100 px-4 py-2">
        <p className="text-[10px] text-neutral-400 leading-relaxed">
          Crown &amp; Rose Craft Standards · v1 · Sources: Continuity Walker, Plot Readiness, Completeness Report, Edit Walker
        </p>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ChecklistGroup({ itemKey, label, deviations, expanded, onToggle, onResolve, onGoToChapter, resolving }) {
  const hasHigh = deviations.some(d => d.severity === 'high');
  return (
    <div className={`rounded-lg border ${hasHigh ? 'border-rose-200' : 'border-amber-200'}`}
         data-testid={`deviation-group-${itemKey}`}>
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-2.5 hover:bg-neutral-50 rounded-lg"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center shrink-0
            ${hasHigh ? 'bg-rose-500 text-white' : 'bg-amber-500 text-white'}`}>
            {itemKey}
          </span>
          <span className="text-[12px] font-semibold text-neutral-800 truncate">{label}</span>
          <span className="text-[10px] text-neutral-500 shrink-0">{deviations.length} open</span>
        </div>
        {expanded ? <ChevronUp className="h-3.5 w-3.5 shrink-0 text-neutral-400" /> : <ChevronDown className="h-3.5 w-3.5 shrink-0 text-neutral-400" />}
      </button>
      {expanded && (
        <div className="border-t border-neutral-100 px-3 pb-2 space-y-2 pt-2">
          {deviations.map(dev => (
            <DeviationRow
              key={dev.id}
              dev={dev}
              onResolve={onResolve}
              onGoToChapter={onGoToChapter}
              resolving={resolving}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DeviationRow({ dev, onResolve, onGoToChapter, resolving }) {
  const srcMeta = SOURCE_META[dev.source] || { label: dev.source, Icon: FileText, color: 'text-neutral-600 bg-neutral-50 border-neutral-200' };
  const { Icon: SrcIcon } = srcMeta;
  const isResolving = resolving[dev.id];
  return (
    <div className={`rounded-md border p-2.5 ${SEV_META[dev.severity] || SEV_META.low}`}
         data-testid={`deviation-row-${dev.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[11.5px] text-neutral-800 leading-snug">{dev.description}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className={`inline-flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-[9.5px] font-medium ${srcMeta.color}`}>
              <SrcIcon className="h-2.5 w-2.5" />{srcMeta.label}
            </span>
            {dev.chapter_number && (
              <span className="rounded border border-neutral-200 bg-white px-1.5 py-0.5 text-[9.5px] text-neutral-600">
                Ch {dev.chapter_number}
              </span>
            )}
            <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider
              ${dev.severity === 'high' ? 'bg-rose-200 text-rose-700' : dev.severity === 'medium' ? 'bg-amber-200 text-amber-700' : 'bg-neutral-200 text-neutral-600'}`}>
              {dev.severity}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-1">
          {dev.chapter_number && onGoToChapter && (
            <button
              onClick={() => onGoToChapter(dev.chapter_number)}
              className="rounded p-1 text-neutral-400 hover:bg-white hover:text-neutral-700"
              title={`Go to Chapter ${dev.chapter_number}`}
              data-testid={`deviation-goto-${dev.id}`}
            >
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            onClick={() => onResolve(dev.id)}
            disabled={isResolving}
            className="rounded p-1 text-neutral-400 hover:bg-white hover:text-emerald-600"
            title="Mark resolved"
            data-testid={`deviation-resolve-${dev.id}`}
          >
            <Check className={`h-3.5 w-3.5 ${isResolving ? 'animate-spin opacity-50' : ''}`} />
          </button>
        </div>
      </div>
    </div>
  );
}

function ResolvedRow({ dev, onReopen, resolving }) {
  return (
    <div className="flex items-center justify-between rounded border border-neutral-100 bg-neutral-50 px-2.5 py-1.5"
         data-testid={`resolved-row-${dev.id}`}>
      <div className="flex items-center gap-2 min-w-0">
        <Check className="h-3 w-3 shrink-0 text-emerald-500" />
        <p className="truncate text-[10.5px] text-neutral-500">{dev.description}</p>
      </div>
      <button
        onClick={() => onReopen(dev.id)}
        disabled={resolving[dev.id]}
        className="ml-2 shrink-0 rounded p-1 text-neutral-400 hover:text-amber-600"
        title="Reopen"
        data-testid={`deviation-reopen-${dev.id}`}
      >
        <RotateCcw className="h-3 w-3" />
      </button>
    </div>
  );
}
